import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useState, useLayoutEffect, Children, isValidElement, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { throttle } from '@hh.ru/magritte-common-func-utils';
import { useResize } from '@hh.ru/magritte-common-resize';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Segment } from './Segment.js';
import { Text } from '@hh.ru/magritte-ui-typography';

var styles = {"segments-container":"magritte-segments-container___0TT81_1-0-47","segmentsContainer":"magritte-segments-container___0TT81_1-0-47","segment-wrapper":"magritte-segment-wrapper___5l67i_1-0-47","segmentWrapper":"magritte-segment-wrapper___5l67i_1-0-47","stretched-container":"magritte-stretched-container___z0WU6_1-0-47","stretchedContainer":"magritte-stretched-container___z0WU6_1-0-47","input":"magritte-input___Vul7O_1-0-47","segment":"magritte-segment___fiYEo_1-0-47","selected":"magritte-selected___HzokM_1-0-47","label":"magritte-label___xwTC5_1-0-47","large-container":"magritte-large-container___HmY5M_1-0-47","largeContainer":"magritte-large-container___HmY5M_1-0-47","icon":"magritte-icon___HGHye_1-0-47","medium-container":"magritte-medium-container___CWfC1_1-0-47","mediumContainer":"magritte-medium-container___CWfC1_1-0-47","small-container":"magritte-small-container___kSY-C_1-0-47","smallContainer":"magritte-small-container___kSY-C_1-0-47","text-wrapper":"magritte-text-wrapper___74ac8_1-0-47","textWrapper":"magritte-text-wrapper___74ac8_1-0-47","invisible-text":"magritte-invisible-text___6gdu9_1-0-47","invisibleText":"magritte-invisible-text___6gdu9_1-0-47","fixed-width-text":"magritte-fixed-width-text___Y3YLq_1-0-47","fixedWidthText":"magritte-fixed-width-text___Y3YLq_1-0-47","free-width-text":"magritte-free-width-text___3WOKl_1-0-47","freeWidthText":"magritte-free-width-text___3WOKl_1-0-47","text":"magritte-text___-0Bi5_1-0-47"};

const LABEL_TYPOGRAPHY = {
    large: {
        selected: 'subtitle-1-semibold',
        unselected: 'label-2-regular',
    },
    medium: {
        selected: 'subtitle-1-semibold',
        unselected: 'label-2-regular',
    },
    small: {
        selected: 'subtitle-2-semibold',
        unselected: 'label-3-regular',
    },
};
const hasEllipsis = (textElement) => {
    if (!textElement.parentElement) {
        return false;
    }
    const textWithoutOverflow = textElement.cloneNode(true);
    textWithoutOverflow.style.position = 'absolute';
    textWithoutOverflow.style.visibility = 'hidden';
    textElement.parentElement.appendChild(textWithoutOverflow);
    try {
        return textWithoutOverflow.clientWidth > textElement.clientWidth;
    }
    finally {
        textWithoutOverflow.remove();
    }
};
/**
 * Для того, чтобы ширина `<Segmented />` не прыгала из-за изменения текста на полужирный в выбранном `<Segment />`,
 * его ширина определяется невидимой оберткой с текстом в обычном начертании.
 * Полужирный текст рисуем абсолютом, чтобы он не влиял на ширину.
 * Если в обычном тексте есть эллипсис, полужирный текст ограничиваем такой же шириной.
 * Если в обычном тексте нет эллипсиса, полужирный текст не ограничиваем по ширине, чтобы в нем тоже не было эллипсиса.
 */
const SelectedSegmentText = ({ children, size }) => {
    const [fixedWidth, setFixedWidth] = useState(null);
    const textElementRef = useRef(null);
    const prevTextWidth = useRef(null);
    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const recalcLayout = useCallback(throttle(() => {
        if (textElementRef.current !== null && textElementRef.current.clientWidth !== prevTextWidth.current) {
            setFixedWidth(hasEllipsis(textElementRef.current));
            prevTextWidth.current = textElementRef.current.clientWidth;
        }
    }, 50), []);
    useLayoutEffect(recalcLayout);
    useResize(recalcLayout);
    return (jsxs("div", { className: styles.textWrapper, children: [jsx("div", { className: classnames({ [styles.invisibleText]: fixedWidth !== null }), children: jsx(Text, { typography: LABEL_TYPOGRAPHY[size].unselected, children: jsx("div", { className: styles.text, ref: textElementRef, children: children }) }) }), fixedWidth !== null && (jsx("div", { className: classnames({ [styles.fixedWidthText]: fixedWidth, [styles.freeWidthText]: !fixedWidth }), children: jsx(Text, { typography: LABEL_TYPOGRAPHY[size].selected, children: jsx("div", { className: styles.text, children: children }) }) }))] }));
};
const SegmentedForwardRefRenderFunc = ({ children, mode, name, onChange, selectedValue, size = 'medium', stretched }, ref) => {
    const id = useId();
    const [actualName, setActualName] = useState(name);
    const forceDisabled = useDisabled();
    useLayoutEffect(() => {
        if (!name) {
            setActualName(id);
        }
    }, [id, name, setActualName]);
    if (forceDisabled) {
        return null;
    }
    return (jsxs("div", { className: classnames(styles.segmentsContainer, {
            [styles[`${size}Container`]]: !!size,
            [styles.stretchedContainer]: mode === 'label' && stretched,
        }), ref: ref, children: [mode === 'icon' &&
                Children.map(children, (child) => {
                    if (!isValidElement(child) || child.type !== Segment) {
                        return null;
                    }
                    const { children: _, disabled, icon: Icon, value, ...props } = child.props;
                    if (!Icon || disabled) {
                        return null;
                    }
                    const selected = selectedValue === value;
                    return (jsxs("label", { className: styles.segmentWrapper, ref: child.ref, children: [jsx("input", { ...props, checked: selected, className: styles.input, name: actualName, onChange: onChange, type: "radio", value: value }), jsx("div", { className: classnames(styles.icon, styles.segment, { [styles.selected]: selected }), children: jsx(Icon, {}) })] }));
                }), mode === 'label' &&
                Children.map(children, (child) => {
                    if (!isValidElement(child) || child.type !== Segment) {
                        return null;
                    }
                    const { children: label, disabled, value, ...props } = child.props;
                    if (!label || disabled) {
                        return null;
                    }
                    const selected = selectedValue === value;
                    return (jsxs("label", { className: styles.segmentWrapper, ref: child.ref, children: [jsx("input", { ...props, checked: selected, className: styles.input, name: actualName, onChange: onChange, type: "radio", value: value }), jsx("div", { className: classnames(styles.label, styles.segment, { [styles.selected]: selected }), children: selected ? (jsx(SelectedSegmentText, { size: size, children: label })) : (jsx(Text, { typography: LABEL_TYPOGRAPHY[size].unselected, children: jsx("div", { className: styles.text, children: label }) })) })] }));
                })] }));
};
const Segmented = forwardRef(SegmentedForwardRefRenderFunc);

export { Segmented };
//# sourceMappingURL=Segmented.js.map
